/**
 * My Followers Page (Leader Dashboard)
 *
 * 나의 팔로워 목록 및 관리 페이지
 * - 팔로워 목록 테이블 (이름, 팔로우 시작일, 할당 금액, 현재 PnL)
 * - 팔로워별 통계 (복사 비율, 총 수익, ROI)
 * - 팔로워 필터링/정렬
 * - 전체 팔로워 통계 요약 카드
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Table, Tag, Avatar, Input, Select, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { formatDecimal } from '@utils/formatDecimal';
import { getBigNumber } from '@utils/getBigNumber';
// Mock import 제거됨 (Real API 사용)

import type { CopyFollow } from '../../../types/copyTrading';

const { Option } = Select;

type FollowerWithStats = {
  follow: CopyFollow & {
    followerUsername: string;
    followerProfileImage: string;
  };
  stats: {
    totalTrades: number;
    totalProfitShared: number;
    roi: number;
  };
};

export function MyFollowersPage() {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useContext(ThemeContext);

  const [followers, setFollowers] = useState<FollowerWithStats[]>([]);
  const [summary, setSummary] = useState({
    activeFollowers: 0,
    totalFollowers: 0,
    totalAllocated: 0,
    totalProfitShared: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  // 필터링/정렬 상태
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 1 | 2 | 3>('all');
  const [sortBy, setSortBy] = useState<
    'name' | 'allocated' | 'pnl' | 'roi' | 'startDate'
  >('startDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const isKo = i18n.language === 'ko';

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    fetchFollowersData();
  }, []);

  // ==========================================
  // Handlers
  // ==========================================

  const fetchFollowersData = async () => {
    setIsLoading(true);
    try {
      const { getMyFollowers } = await import('../../api/copyTrading');
      const response = await getMyFollowers();
      console.log('response', response);

      if (response.success) {
        const items = response.data?.items || [];

        // FollowerWithStats 구조로 변환
        const followersWithStats = items.map((item: any) => ({
          follow: {
            ...item,
            // 유저 정보가 없을 경우 기본값 제공
            followerUsername:
              item.followerUsername ||
              item.username ||
              `User ${item.followerUserId}`,
            followerAvatar: item.followerAvatar || item.avatar || '',
          },
          stats: {
            totalTrades: 0,
            totalProfitShared: item.totalProfitShared || 0,
            roi: 0,
          }, // MVP: stats는 추후 구현
        }));

        setFollowers(followersWithStats);

        // Summary 계산
        const totalAllocated = items.reduce(
          (sum: number, item: any) =>
            sum + parseFloat(item.allocatedAmount || '0'),
          0
        );

        setSummary({
          activeFollowers: items.filter((item: any) => item.status === 1)
            .length,
          totalFollowers: items.length,
          totalAllocated,
          totalProfitShared: 0, // MVP
        });
      } else {
        setFollowers([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch followers:', error);
      setFollowers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // Computed Values (필터링 & 정렬)
  // ==========================================

  const filteredAndSortedFollowers = useMemo(() => {
    let result = [...followers];

    // 검색 필터
    if (searchText) {
      result = result.filter((item) => {
        const username = item.follow.followerUsername || '';
        return username.toLowerCase().includes(searchText.toLowerCase());
      });
    }

    // 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter((item) => item.follow.status === statusFilter);
    }

    // 정렬
    result.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'name':
          const nameA = a.follow.followerUsername || '';
          const nameB = b.follow.followerUsername || '';
          compareValue = nameA.localeCompare(nameB);
          break;
        case 'allocated':
          compareValue = a.follow.allocatedAmount - b.follow.allocatedAmount;
          break;
        case 'pnl':
          compareValue = a.follow.currentPnl - b.follow.currentPnl;
          break;
        case 'roi':
          compareValue = (a.stats?.roi || 0) - (b.stats?.roi || 0);
          break;
        case 'startDate':
          compareValue =
            new Date(a.follow.startedAt).getTime() -
            new Date(b.follow.startedAt).getTime();
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return result;
  }, [followers, searchText, statusFilter, sortBy, sortOrder]);

  // 평균 ROI 계산
  const averageRoi = useMemo(() => {
    if (followers.length === 0) return 0;
    const totalRoi = followers.reduce(
      (sum, item) => sum + (item.stats?.roi || 0),
      0
    );
    return totalRoi / followers.length;
  }, [followers]);

  // ==========================================
  // Table Columns
  // ==========================================

  const columns: ColumnsType<FollowerWithStats> = useMemo(
    () => [
      {
        title: t('followerUsername'),
        key: 'follower',
        render: (_, record) => {
          const username = record.follow?.followerUsername || 'Unknown';
          const profileImage = record.follow?.followerProfileImage;
          const imageUrl = profileImage
            ? `${import.meta.env.VITE_APP_API_MEDIA_DEV_URL}/${profileImage}`
            : '';

          return (
            <FollowerInfo>
              <Avatar src={imageUrl} size={40}>
                {username.charAt(0).toUpperCase()}
              </Avatar>
              <FollowerName>
                <div style={{ fontWeight: 600 }}>{username}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {new Date(record.follow.startedAt).toLocaleDateString()}
                </div>
              </FollowerName>
            </FollowerInfo>
          );
        },
      },
      {
        title: t('status'),
        key: 'status',
        render: (_, record) => {
          const statusMap = {
            1: { text: t('active'), color: 'green' },
            2: { text: t('paused'), color: 'orange' },
            3: { text: t('stopped'), color: 'red' },
          };
          const status = statusMap[record.follow.status as 1 | 2 | 3];
          return <Tag color={status.color}>{status.text}</Tag>;
        },
      },
      {
        title: t('copyRatio'),
        key: 'copyRatio',
        render: (_, record) =>
          `${formatDecimal(record.follow.copyRatio * 100, 0)}%`,
      },
      {
        title: t('allocatedAmount'),
        key: 'allocatedAmount',
        render: (_, record) =>
          `${formatDecimal(record.follow.allocatedAmount, 2)} USDT`,
        sorter: true,
      },
      {
        title: t('currentPnl'),
        key: 'currentPnl',
        render: (_, record) => {
          const isPositive = record.follow.currentPnl >= 0;
          return (
            <span
              style={{
                color: isPositive ? '#22c55e' : '#ef4444',
                fontWeight: 600,
              }}
            >
              {isPositive ? '+' : ''}
              {formatDecimal(record.follow.currentPnl, 2)} USDT
            </span>
          );
        },
        sorter: true,
      },
      {
        title: t('roi'),
        key: 'roi',
        render: (_, record) => {
          const roi = record.stats?.roi || 0;
          const isPositive = roi >= 0;
          return (
            <span
              style={{
                color: isPositive ? '#22c55e' : '#ef4444',
                fontWeight: 600,
              }}
            >
              {isPositive ? '+' : ''}
              {formatDecimal(roi, 2)}%
            </span>
          );
        },
        sorter: true,
      },
      {
        title: t('profitShared'),
        key: 'profitShared',
        render: (_, record) => {
          const profitShared = record.stats?.totalProfitShared || 0;
          return (
            <span style={{ color: '#22c55e', fontWeight: 600 }}>
              +{formatDecimal(profitShared, 2)} USDT
            </span>
          );
        },
      },
    ],
    [t]
  );

  // ==========================================
  // Render
  // ==========================================

  return (
    <PageWrapper>
      {/* Page Header */}
      <CompactHeader isDark={isDarkMode}>
        <HeaderLeft>
          <HeaderTitle>{t('myFollowers')}</HeaderTitle>
          <HeaderDescription>{t('myFollowersDesc')}</HeaderDescription>
        </HeaderLeft>

        <StatsRow>
          <StatItem>
            <StatLabel>{t('total')}</StatLabel>
            <StatValue>{followers.length}</StatValue>
          </StatItem>
          <StatDivider />
          <StatItem>
            <StatLabel>{t('active')}</StatLabel>
            <StatValue style={{ color: '#22c55e' }}>
              {summary.activeFollowers}
            </StatValue>
          </StatItem>
        </StatsRow>
      </CompactHeader>

      {/* Summary Cards */}
      <SummaryGrid>
        <SummaryCard isDark={isDarkMode}>
          <SummaryLabel>{t('totalFollowers')}</SummaryLabel>
          <SummaryValue>{followers.length}</SummaryValue>
          <SummarySubtext>
            {t('active')}: {summary.activeFollowers}
          </SummarySubtext>
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <SummaryLabel>{t('totalAllocatedAmount')}</SummaryLabel>
          <SummaryValue>
            {formatDecimal(summary.totalAllocated, 2)} USDT
          </SummaryValue>
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <SummaryLabel>{t('profitShareAmountTotal')}</SummaryLabel>
          <SummaryValue style={{ color: '#22c55e' }}>
            +{formatDecimal(summary.totalProfitShared, 2)} USDT
          </SummaryValue>
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <SummaryLabel>{t('averageRoi')}</SummaryLabel>
          <SummaryValue
            style={{ color: averageRoi >= 0 ? '#22c55e' : '#ef4444' }}
          >
            {averageRoi >= 0 ? '+' : ''}
            {formatDecimal(averageRoi, 2)}%
          </SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      {/* Filters & Search */}
      <FilterSection isDark={isDarkMode}>
        <Input
          placeholder={t('searchFollowerName')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 250 }}
        />

        <FilterGroup>
          <label>{t('status')}:</label>
          <Select
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            style={{ width: 120 }}
          >
            <Option value="all">{t('all')}</Option>
            <Option value={1}>{t('active')}</Option>
            <Option value={2}>{t('paused')}</Option>
            <Option value={3}>{t('stopped')}</Option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>{t('sort')}:</label>
          <Select
            value={sortBy}
            onChange={(value) => setSortBy(value)}
            style={{ width: 150 }}
          >
            <Option value="startDate">{t('startDate')}</Option>
            <Option value="name">{t('name')}</Option>
            <Option value="allocated">{t('allocatedAmount')}</Option>
            <Option value="pnl">{t('profitAmount')}</Option>
            <Option value="roi">{t('profitRate')}</Option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>{t('sortOrder')}:</label>
          <Select
            value={sortOrder}
            onChange={(value) => setSortOrder(value)}
            style={{ width: 100 }}
          >
            <Option value="desc">{t('descending')}</Option>
            <Option value="asc">{t('ascending')}</Option>
          </Select>
        </FilterGroup>
      </FilterSection>

      {/* Followers Table */}
      <Section isDark={isDarkMode}>
        <SectionTitle>
          {t('followersList')}
          <CountBadge>
            {filteredAndSortedFollowers.length} {isKo ? '명' : 'followers'}
          </CountBadge>
        </SectionTitle>
        <Table
          columns={columns}
          dataSource={filteredAndSortedFollowers}
          rowKey={(record) =>
            record?.follow?.id || (record as any)?.id || Math.random()
          }
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) =>
              isKo ? `총 ${total}명` : `Total ${total}peoples`,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Section>
    </PageWrapper>
  );
}

// ==========================================
// Styled Components
// ==========================================

const PageWrapper = styled.div`
  display: flex;
  max-width: 1200px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  @media (max-width: 1240px) {
    padding: 20px;
  }
`;

const CompactHeader = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
  padding: 20px 24px;
  box-sizing: border-box;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.05) 0%, rgba(20, 17, 34, 0.05) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const HeaderDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 20px;
  justify-self: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
    justify-self: stretch;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 80px;

  @media (max-width: 768px) {
    align-items: center;
    min-width: unset;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.text};
  opacity: 0.1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SummaryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div<{ isDark: boolean }>`
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(20, 17, 34, 0.03) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.02) 0%, rgba(255, 255, 255, 1) 100%)'};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #f0f0f0')};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ isDark }) =>
      isDark
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
  margin-bottom: 8px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  margin-bottom: 4px;
`;

const SummarySubtext = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
`;

const FilterSection = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  padding: 16px 20px;
  box-sizing: border-box;
  background: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 14px;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
  }
`;

const Section = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-bottom: 32px;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CountBadge = styled.span`
  font-size: 14px;
  font-weight: 500;
  padding: 4px 12px;
  background: ${({ theme }) => theme.COLORS.tabMenuActive};
  color: #fff;
  border-radius: 12px;
`;

const FollowerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FollowerName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
